import React, { useState, useRef, useEffect } from "react";
import { useFormik } from "formik";
import {
  X,
  Building2,
  Phone,
  Mail,
  Globe,
  MapPin,
  Check,
  AlertCircle,
  Info,
  Loader2,
  Image as ImageIcon,
  Plus,
  Edit2,
} from "lucide-react";
import { toast } from "react-toastify";
import { isPossiblePhoneNumber } from "libphonenumber-js";
import organizerService from "../../../../../api/OrganizerService";

import ImageUpload from "../../organizer/components/ImageUpload";

const normalizePhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return phoneNumber;

  let cleaned = phoneNumber.replace(/[\s\-()]/g, "");

  if (cleaned.startsWith("0")) {
    cleaned = "233" + cleaned.substring(1);
  } else if (cleaned.startsWith("+233")) {
    cleaned = cleaned.substring(1);
  }

  return cleaned;
};

/**
 * Form Validation Schema
 */
const validate = (values) => {
  const errors = {};

  // Name validation
  if (!values.name) {
    errors.name = "Organizer name is required";
  } else if (values.name.length < 3) {
    errors.name = "Name must be at least 3 characters";
  } else if (values.name.length > 100) {
    errors.name = "Name must not exceed 100 characters";
  }

  // Phone validation
  if (!values.phoneNumber) {
    errors.phoneNumber = "Phone number is required";
  } else {
    const cleanedNumber = values.phoneNumber.replace(/[\s\-()]/g, "");

    const isValidInternational = isPossiblePhoneNumber(cleanedNumber);
    const isValidLocalFormat = /^(\+?[0-9]\d{6,15})$/.test(cleanedNumber);

    if (!isValidInternational && !isValidLocalFormat) {
      errors.phoneNumber = "Please enter a valid phone number";
    } else if (cleanedNumber.length < 7) {
      errors.phoneNumber = "Phone number is too short";
    } else if (cleanedNumber.length > 16) {
      errors.phoneNumber = "Phone number is too long";
    }
  }

  // Email validation
  if (!values.email) {
    errors.email = "Email address is required";
  } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
    errors.email = "Invalid email address";
  }

  // Country validation
  if (!values.country) {
    errors.country = "Country is required";
  }

  // City validation
  if (!values.city) {
    errors.city = "City is required";
  } else if (values.city.length < 2) {
    errors.city = "City must be at least 2 characters";
  }

  return errors;
};

/**
 * Create/Edit Organizer Modal Component
 * Allows creating or editing organizers directly from the event creation flow
 */
const CreateEditOrganizerModal = ({
  isOpen,
  onClose,
  organizer = null,
  onSuccess,
}) => {
  const isEditMode = Boolean(organizer);
  const logoChangedRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Formik setup
  const formik = useFormik({
    initialValues: {
      name: organizer?.name || "",
      phoneNumber: organizer?.phone_number || "",
      email: organizer?.email || "",
      country: organizer?.country || "",
      city: organizer?.city || "",
      logo: null,
    },
    validate,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setIsSubmitting(true);

      try {
        const normalizedPhoneNumber = normalizePhoneNumber(values.phoneNumber);
        let logoValue = values.logo;
        if (isEditMode && !logoChangedRef.current && organizer?.logo) {
          logoValue = organizer.logo;
        }

        // Build payload
        const payload = {
          name: values.name.trim(),
          phone_number: normalizedPhoneNumber,
          email: values.email.trim().toLowerCase(),
          country: values.country.trim(),
          city: values.city.trim(),
          logo: logoValue,
        };

        let response;
        if (isEditMode) {
          response = await organizerService.updateOrganizer(
            organizer?.slug || "",
            payload
          );
        } else {
          response = await organizerService.createOrganizer(payload);
        }

        if (!response.data?.status_code === 200) {
          toast.error(
            response.data?.message || "An error occurred. Please try again."
          );
          return;
        }

        toast.success(
          response?.data?.data?.message ||
            `Organizer ${isEditMode ? "updated" : "created"} successfully`
        );

        if (onSuccess) {
          const updatedOrganizer = response.data?.data?.data ||
            response.data?.data || {
              ...organizer,
              ...payload,
              slug: organizer?.slug || response.data?.data?.slug,
            };
          onSuccess(updatedOrganizer, isEditMode);
        }

        formik.resetForm();
        logoChangedRef.current = false;
        onClose();
      } catch (error) {
        console.error("Error submitting form:", error);
        toast.error(
          error.response?.data?.reason || "An error occurred. Please try again."
        );
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (!isOpen) {
      formik.resetForm();
      logoChangedRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="flex fixed inset-0 z-[60] justify-center items-center p-4 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={isSubmitting ? undefined : onClose}
      />
      <div className="relative w-full max-w-3xl max-h-[90vh] bg-white rounded-2xl shadow-2xl duration-200 animate-in fade-in-0 zoom-in-95 overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 px-6 py-5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-t-2xl">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white bg-opacity-20 rounded-lg backdrop-blur">
                {isEditMode ? (
                  <Edit2 className="w-6 h-6 text-white" />
                ) : (
                  <Plus className="w-6 h-6 text-white" />
                )}
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">
                  {isEditMode ? "Edit Organizer" : "Create New Organizer"}
                </h3>
                <p className="text-sm text-blue-100">
                  {isEditMode
                    ? "Update organizer information"
                    : "Add a new organizer to your list"}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-shrink-0 p-2 text-white rounded-lg transition-colors hover:text-blue-200 hover:bg-white hover:bg-opacity-20 disabled:opacity-50"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* UX Guidelines Banner */}
          <div className="p-3 mb-6 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start space-x-2">
              <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-blue-800">
                <p className="mb-0.5 font-semibold">💡 Quick Tips:</p>
                <ul className="space-y-0.5 text-xs list-disc list-inside">
                  <li>Use high-quality logos (PNG or JPG, max 5MB)</li>
                  <li>
                    Phone numbers can be local (0249158582) or international
                    (+233249158582)
                  </li>
                  <li>All fields marked with * are required</li>
                  <li>Double-check email addresses for accuracy</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={formik.handleSubmit} className="space-y-6">
            {/* Main Information Card */}
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
              <h2 className="flex items-center mb-4 text-base font-bold text-gray-900">
                <Building2 className="mr-2 w-4 h-4 text-blue-600" />
                Organizer Information
              </h2>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {/* Organizer Name */}
                <div className="sm:col-span-2">
                  <label className="block mb-1.5 text-xs font-semibold text-gray-700">
                    Organizer Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    placeholder="e.g., Global Events Company"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      formik.touched.name && formik.errors.name
                        ? "border-red-300 bg-red-50"
                        : "border-gray-200 bg-white focus:bg-white"
                    }`}
                  />
                  {formik.touched.name && formik.errors.name && (
                    <div className="flex items-center mt-1 text-xs text-red-600">
                      <AlertCircle className="mr-1 w-3 h-3" />
                      {formik.errors.name}
                    </div>
                  )}
                </div>

                {/* Phone Number */}
                <div>
                  <label className="block mb-1.5 text-xs font-semibold text-gray-700">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-2.5 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
                    <input
                      type="tel"
                      name="phoneNumber"
                      placeholder="0249158582, +233249158582"
                      value={formik.values.phoneNumber}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        formik.touched.phoneNumber && formik.errors.phoneNumber
                          ? "border-red-300 bg-red-50"
                          : "border-gray-200 bg-white focus:bg-white"
                      }`}
                    />
                  </div>
                  {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                    <div className="flex items-center mt-1 text-xs text-red-600">
                      <AlertCircle className="mr-1 w-3 h-3" />
                      {formik.errors.phoneNumber}
                    </div>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block mb-1.5 text-xs font-semibold text-gray-700">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-2.5 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
                    <input
                      type="email"
                      name="email"
                      placeholder="contact@example.com"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        formik.touched.email && formik.errors.email
                          ? "border-red-300 bg-red-50"
                          : "border-gray-200 bg-white focus:bg-white"
                      }`}
                    />
                  </div>
                  {formik.touched.email && formik.errors.email && (
                    <div className="flex items-center mt-1 text-xs text-red-600">
                      <AlertCircle className="mr-1 w-3 h-3" />
                      {formik.errors.email}
                    </div>
                  )}
                </div>

                {/* Country */}
                <div>
                  <label className="block mb-1.5 text-xs font-semibold text-gray-700">
                    Country <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-2.5 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
                    <input
                      type="text"
                      name="country"
                      placeholder="e.g., Ghana, Nigeria"
                      value={formik.values.country}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        formik.touched.country && formik.errors.country
                          ? "border-red-300 bg-red-50"
                          : "border-gray-200 bg-white focus:bg-white"
                      }`}
                    />
                  </div>
                  {formik.touched.country && formik.errors.country && (
                    <div className="flex items-center mt-1 text-xs text-red-600">
                      <AlertCircle className="mr-1 w-3 h-3" />
                      {formik.errors.country}
                    </div>
                  )}
                </div>

                {/* City */}
                <div>
                  <label className="block mb-1.5 text-xs font-semibold text-gray-700">
                    City <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-2.5 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
                    <input
                      type="text"
                      name="city"
                      placeholder="e.g., Accra"
                      value={formik.values.city}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        formik.touched.city && formik.errors.city
                          ? "border-red-300 bg-red-50"
                          : "border-gray-200 bg-white focus:bg-white"
                      }`}
                    />
                  </div>
                  {formik.touched.city && formik.errors.city && (
                    <div className="flex items-center mt-1 text-xs text-red-600">
                      <AlertCircle className="mr-1 w-3 h-3" />
                      {formik.errors.city}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Logo Upload Card */}
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
              <h2 className="flex items-center mb-2 text-base font-bold text-gray-900">
                <ImageIcon className="mr-2 w-4 h-4 text-blue-600" />
                Organizer Logo
              </h2>
              <p className="mb-4 text-xs text-gray-600">
                Upload a logo for the organizer. Recommended size: 512x512px
                (PNG, JPG)
              </p>

              <ImageUpload
                value={formik.values.logo}
                onChange={(value) => {
                  logoChangedRef.current = true;
                  formik.setFieldValue("logo", value);
                }}
                previewUrl={organizer?.logo}
                maxSize={5}
                acceptedFormats={[
                  "image/jpeg",
                  "image/jpg",
                  "image/png",
                  "image/webp",
                ]}
              />
            </div>

            {/* Form Actions */}
            <div className="flex flex-col gap-2 pt-4 border-t border-gray-200 sm:flex-row sm:gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2.5 text-sm text-gray-700 bg-white border-2 border-gray-200 hover:bg-gray-50 rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !formik.isValid}
                className="flex-1 px-4 py-2.5 text-sm bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg font-semibold transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                    {isEditMode ? "Updating..." : "Creating..."}
                  </>
                ) : (
                  <>
                    <Check className="mr-2 w-4 h-4" />
                    {isEditMode ? "Update Organizer" : "Create Organizer"}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateEditOrganizerModal;
